// ---------------------------------------------------------------------------
// step-2-scratch.tsx — Design system selection step for "scratch" path.
// Lists user presets as selectable cards. Optionally expands inline "Create DS"
// panel with the 6-axis DesignSystemControls and a name input.
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from 'react'
import styles from '../new-project-wizard-page.module.css'
import { useWizard } from '../wizard-state'
import {
  fetchPresetsViaWorker,
  type PresetListItem,
} from '@/features/design-system/lib/fetch-presets-via-worker'
import {
  fetchDesignSystemReleasesViaWorker,
  projectReleaseBinding,
  type DesignSystemReleaseOption,
} from '@/features/design-system/lib/fetch-design-system-releases-via-worker'
import { saveDesignSystemToLibrary } from '@/features/design-system/lib/save-design-system-library'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import {
  STYLE_OPTIONS,
  BASE_COLOR_OPTIONS,
  THEME_OPTIONS,
  CHART_OPTIONS,
  RADIUS_OPTIONS,
  FONT_OPTIONS,
  type DesignSystemState,
  type StyleKey,
  type BaseColorKey,
  type ThemeKey,
  type ChartKey,
  type RadiusKey,
  type FontKey,
  DEFAULT_DESIGN_SYSTEM_STATE,
} from '@/features/design-system/lib/design-system-options'

// Inline DS axes select — minimal version, doesn't need the full sidebar layout.
//
// The baseline is imported, not restated. This file used to declare its own
// (`style: 'luma'`) while the design-system editor declared another
// (`style: 'lyra'`), and the platform's canonical theme is built from a third —
// so which default look an app got depended on which door its creator came
// through. One baseline now, derived from the canonical theme.
const DEFAULT_DS_STATE: DesignSystemState = DEFAULT_DESIGN_SYSTEM_STATE

interface AxisSelectProps {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}

function AxisSelect({ label, value, options, onChange }: AxisSelectProps) {
  return (
    <div className={styles.axisRow}>
      <label className={styles.axisLabel}>{label}</label>
      <select
        className={styles.axisSelect}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function Step2Scratch() {
  const copy = useStudioLocale().strings.newProject.step2Scratch
  const { state, dispatch } = useWizard()
  const designSystem = useStudioStore((s) => s.designSystem)

  const [presets, setPresets] = useState<PresetListItem[] | null>(null)
  const [releases, setReleases] = useState<DesignSystemReleaseOption[] | null>(null)
  const [releaseError, setReleaseError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newDsName, setNewDsName] = useState(copy.defaultStyleName)
  const [newDsDescription, setNewDsDescription] = useState('')
  const [newDsState, setNewDsState] = useState<DesignSystemState>(
    designSystem ?? DEFAULT_DS_STATE,
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetchDesignSystemReleasesViaWorker(),
      fetchPresetsViaWorker().catch(() => []),
    ]).then(([releaseResult, presetList]) => {
      if (cancelled) return
      setReleases(releaseResult.releases)
      setReleaseError(releaseResult.error ?? null)
      setPresets(presetList)
    })
    return () => {
      cancelled = true
    }
  }, [])

  function selectPreset(p: PresetListItem) {
    if (state.starterPathId && !p.tokens) return
    dispatch({
      type: 'SELECT_PRESET',
      id: p.id,
      name: p.name,
      tokens: p.tokens,
    })
    setCreateOpen(false)
  }

  function selectRelease(release: DesignSystemReleaseOption) {
    const binding = projectReleaseBinding(release)
    if (!binding) return
    dispatch({
      type: 'SELECT_DESIGN_SYSTEM_RELEASE',
      release: binding,
      name: release.name,
    })
    setCreateOpen(false)
  }

  async function handleSaveNewDs() {
    if (!newDsName.trim()) {
      setSaveError(copy.enterStyleName)
      return
    }
    const description = newDsDescription.trim()
    if (!description) {
      setSaveError(copy.enterStyleDescription)
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const result = await saveDesignSystemToLibrary({
        name: newDsName.trim(),
        description,
        tokens: newDsState,
      })
      if (!result.success || !result.data) {
        setSaveError(result.error ?? copy.saveFailed)
        return
      }
      // Add to local list + auto-select
      const newItem: PresetListItem = {
        id: result.data.id,
        name: result.data.name,
        // Older platform responses omitted description. Fall back only when
        // the field is absent; a server-returned empty string is authoritative.
        description: result.data.description ?? description,
        tokens: newDsState,
      }
      setPresets((prev) => (prev ? [newItem, ...prev] : [newItem]))
      dispatch({
        type: 'SELECT_PRESET',
        id: result.data.id,
        name: result.data.name,
        tokens: newDsState,
      })
      setCreateOpen(false)
      setNewDsName(copy.defaultStyleName)
      setNewDsDescription('')
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : copy.unknownError)
    } finally {
      setSaving(false)
    }
  }

  function setAxis<K extends keyof DesignSystemState>(
    key: K,
    value: DesignSystemState[K],
  ) {
    setNewDsState((prev) => ({ ...prev, [key]: value }))
  }

  const eligibleProjectReleases = (releases ?? []).filter(
    (release) =>
      release.activationScopes.includes('project') &&
      state.runtimeTargets.every((target) => release.targetPlatforms.includes(target)),
  )

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeading}>
        <h1 className={styles.stepH1}>{copy.heading}</h1>
        <p className={styles.stepLead}>{copy.lead}</p>
      </div>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{copy.approvedSystems}</h2>

        {!releases && (
          <div className={styles.loadingRow}>
            <span className={styles.spinner} aria-hidden="true" />
            {copy.loadingReleases}
          </div>
        )}

        {releaseError && (
          <div className={styles.errorBanner} role="alert">
            {releaseError}
          </div>
        )}

        {releases && (
          <>
            {eligibleProjectReleases.length === 0 && (
              <div
                className={styles.emptyState}
                data-testid="new-project-no-project-design-releases"
              >
                {copy.noProjectRelease(state.runtimeTargets.join(', '))}
              </div>
            )}
            <div className={styles.presetList}>
              {eligibleProjectReleases.map((release) => {
                  const isSelected = state.selectedDesignSystemRelease?.releaseId === release.releaseId
                  return (
                    <button
                      key={release.releaseId}
                      type="button"
                      className={[
                        styles.presetCard,
                        isSelected ? styles.presetCardSelected : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => selectRelease(release)}
                      aria-pressed={isSelected}
                      data-testid={`new-project-design-release-${release.releaseId}`}
                    >
                      <span className={styles.presetCardName}>
                        {release.name} · {release.packageVersion}
                      </span>
                      <span className={styles.presetCardDesc}>
                        {release.targetPlatforms.join(', ')} ·{' '}
                        {copy.immutableRelease}
                      </span>
                      {isSelected && (
                        <span className={styles.presetCardCheck} aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </button>
                  )
                })}
            </div>
          </>
        )}
      </section>

      {/* Mutable personal presets */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{copy.personalStyles}</h2>

        {!presets && (
          <div className={styles.loadingRow}>
            <span className={styles.spinner} aria-hidden="true" />
            {copy.loading}
          </div>
        )}

        {presets && presets.length === 0 && !createOpen && (
          <div className={styles.emptyState}>{copy.noPersonalStyle}</div>
        )}

        {presets && presets.length > 0 && (
          <div className={styles.presetList}>
            {presets.map((p) => {
              const isSelected = state.selectedPresetId === p.id
              const isGenerationCompatible = !state.starterPathId || Boolean(p.tokens)
              return (
                <button
                  key={p.id}
                  type="button"
                  className={[
                    styles.presetCard,
                    isSelected ? styles.presetCardSelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectPreset(p)}
                  aria-pressed={isSelected}
                  disabled={!isGenerationCompatible}
                  data-testid={`new-project-preset-${p.id}`}
                >
                  <span className={styles.presetCardName}>{p.name}</span>
                  {p.description && (
                    <span className={styles.presetCardDesc}>
                      {p.description}
                    </span>
                  )}
                  {!isGenerationCompatible && (
                    <span className={styles.presetCardDesc}>
                      {copy.resaveLegacy}
                    </span>
                  )}
                  {isSelected && (
                    <span className={styles.presetCardCheck} aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </section>

      {/* Create new DS */}
      <section className={styles.section}>
        <button
          type="button"
          className={styles.expandToggle}
          onClick={() => setCreateOpen((v) => !v)}
          aria-expanded={createOpen}
          data-testid="new-project-create-design-system"
        >
          <span>{createOpen ? '−' : '+'}</span>
          <span>{copy.createNewStyle}</span>
        </button>

        {createOpen && (
          <div className={styles.createDsPanel}>
            <div className={styles.createDsNameRow}>
              <label className={styles.fieldLabel} htmlFor="ds-name">
                {copy.nameLabel}
              </label>
              <input
                id="ds-name"
                type="text"
                className={styles.fieldInput}
                value={newDsName}
                onChange={(e) => setNewDsName(e.target.value)}
                placeholder={copy.defaultStyleName}
              />
            </div>

            <div className={styles.createDsNameRow}>
              <label className={styles.fieldLabel} htmlFor="ds-description">
                {copy.descriptionLabel}
              </label>
              <textarea
                id="ds-description"
                className={styles.fieldTextarea}
                value={newDsDescription}
                onChange={(e) => setNewDsDescription(e.target.value)}
                placeholder={copy.descriptionPlaceholder}
                rows={3}
                required
              />
            </div>

            <div className={styles.axisGrid}>
              <AxisSelect
                label={copy.axis.style}
                value={newDsState.style}
                options={STYLE_OPTIONS}
                onChange={(v) => setAxis('style', v as StyleKey)}
              />
              <AxisSelect
                label={copy.axis.baseColor}
                value={newDsState.baseColor}
                options={BASE_COLOR_OPTIONS}
                onChange={(v) => setAxis('baseColor', v as BaseColorKey)}
              />
              <AxisSelect
                label={copy.axis.theme}
                value={newDsState.theme}
                options={THEME_OPTIONS}
                onChange={(v) => setAxis('theme', v as ThemeKey)}
              />
              <AxisSelect
                label={copy.axis.chart}
                value={newDsState.chart}
                options={CHART_OPTIONS}
                onChange={(v) => setAxis('chart', v as ChartKey)}
              />
              <AxisSelect
                label={copy.axis.radius}
                value={newDsState.radius}
                options={RADIUS_OPTIONS}
                onChange={(v) => setAxis('radius', v as RadiusKey)}
              />
              <AxisSelect
                label={copy.axis.font}
                value={newDsState.font}
                options={FONT_OPTIONS}
                onChange={(v) => setAxis('font', v as FontKey)}
              />
            </div>

            {saveError && (
              <div className={styles.errorBanner} role="alert">
                {saveError}
              </div>
            )}

            <div className={styles.createDsActions}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => {
                  setCreateOpen(false)
                  setSaveError(null)
                }}
                disabled={saving}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => void handleSaveNewDs()}
                disabled={saving || !newDsName.trim() || !newDsDescription.trim()}
              >
                {saving ? copy.saving : copy.saveAndContinue}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
